import { Form, Input, Modal, Select } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import dayjsGenerateConfig from 'rc-picker/lib/generate/dayjs';
import generatePicker from 'antd/es/date-picker/generatePicker';
import { useEffect, useMemo, useState } from 'react';
import useDimOptionsData from '../hooks/useDimOptionsData';
import useCreatePlanMutation from '../hooks/useCreatePlanMutation';
import useEditPlanMutation from '../hooks/useEditPlanMutation';
import { isEmpty } from 'lodash';
import type { PlanRecord } from '../types/plan';
import { useTpoGenConfig } from '../context/CustomerConfigContext';
import type {
    FormFieldConfig,
    HiddenWhenConfig,
    DimSelectFieldConfig,
    SelectFieldConfig,
    YearPickerFieldConfig,
} from '../config/tpoGenConfig';
import type { CreatePlanPayload } from '../hooks/useCreatePlanMutation';
import { buildPlanPayload } from '../utils/buildPlanPayload';
import { DimSelect } from './DimSelect';
import type { UseDimOptionsHook, UseDimOptionsParams } from './DimSelect';

const DatePicker = generatePicker<Dayjs>(dayjsGenerateConfig);

const YEAR_DISABLED_DATE_FNS: Record<string, (date: Dayjs) => boolean> = {
    currentAndNext: (date) => !(date.year() === dayjs().year() || date.year() === dayjs().year() + 1),
};

const usePlanDimOptions: UseDimOptionsHook = (params: UseDimOptionsParams) =>
    useDimOptionsData({
        dimension: params.dimension,
        attributeType: params.attributeType,
        selectedDimension: params.selectedDimension,
        enabled: params.enabled ?? true,
        searchString: params.searchString ?? '',
    });

const isFieldHidden = (
    hiddenWhen: HiddenWhenConfig | undefined,
    createMode: string,
    isEditMode: boolean,
): boolean => {
    if (!hiddenWhen) return false;
    if (hiddenWhen.isEdit && isEditMode) return true;
    if (hiddenWhen.modes?.includes(createMode)) return true;
    return false;
};

const CreatePlanModal = ({
    open,
    createMode,
    planningGroupDetails,
    editPlanDetails,
    onCancel,
    onSuccessHandler,
}: {
    open: boolean;
    createMode: 'quick-create' | 'planning-group-create' | 'plan-create';
    planningGroupDetails: any;
    editPlanDetails?: PlanRecord | null;
    onCancel: () => void;
    onSuccessHandler?: () => void;
}) => {
    const tpoGenConfig = useTpoGenConfig();
    const [form] = Form.useForm();
    const [isOkDisabled, setIsOkDisabled] = useState(true);

    const isEditMode = editPlanDetails != null;
    const isPlanningGroupCreateMode = createMode === 'planning-group-create' && !isEmpty(planningGroupDetails);

    const visibleFields = useMemo(
        () =>
            tpoGenConfig.planFields.filter(
                (field: FormFieldConfig) => !isFieldHidden(field.hiddenWhen, createMode, isEditMode),
            ),
        [createMode, isEditMode],
    );

    useEffect(() => {
        if (!open) return;
        if (isEditMode) {
            form.setFieldsValue({
                planName: editPlanDetails.planning_group_name,
                planVersion: editPlanDetails.plan_version,
            });
            setIsOkDisabled(false);
        } else {
            form.resetFields();
            setIsOkDisabled(true);
        }
    }, [open, isEditMode, editPlanDetails, form]);

    const onSuccess = () => {
        form.resetFields();
        onSuccessHandler?.();
        onCancel();
    };

    const { mutate: createPlan, isPending: createPlanPending } = useCreatePlanMutation({ onSuccess });
    const { mutate: editPlan, isPending: editPlanPending } = useEditPlanMutation({ onSuccess });

    const isPending = isEditMode ? editPlanPending : createPlanPending;

    const handleOk = () => {
        form.submit();
    };

    const handleFieldsChange = () => {
        const fields = form.getFieldsError();
        const values = form.getFieldsValue();
        const hasErrors = fields.some(({ errors }) => errors.length > 0);
        const hasEmptyRequired = Object.entries(values).some(
            ([, value]) => value === undefined || value === null || value === '',
        );
        setIsOkDisabled(hasErrors || hasEmptyRequired);
    };

    const handleFinish = (values: Record<string, any>) => {
        if (isEditMode) {
            editPlan({
                planId: editPlanDetails.planning_group_id,
                fields: [
                    {
                        field_id: 'plan_name',
                        field_label: 'Plan Name',
                        value: values.planName,
                    },
                    {
                        field_id: 'plan_version',
                        field_label: 'Plan Version',
                        value: values.planVersion,
                    },
                ],
            });
            return;
        }

        const payload = buildPlanPayload(tpoGenConfig.planFields, values) as CreatePlanPayload;

        if (isPlanningGroupCreateMode) {
            payload.dim1_attrs = planningGroupDetails.dim1_attrs;
            payload.dim2_attrs = planningGroupDetails.dim2_attrs;
            payload.plan_year = planningGroupDetails.plan_year;
        }

        if (payload.dim2_attrs && !Array.isArray(payload.dim2_attrs)) {
            payload.dim2_attrs = [payload.dim2_attrs as string];
        }

        createPlan(payload);
    };

    const dim1Value = Form.useWatch('dim1', form);

    const renderField = (field: FormFieldConfig, index: number) => {
        const rules = field.required
            ? [{ required: true, message: `${field.label} is required` }]
            : undefined;

        switch (field.type) {
            case 'input':
                return (
                    <Form.Item key={index} label={field.label} name={field.name} rules={rules}>
                        <Input placeholder={field.placeholder} />
                    </Form.Item>
                );
            case 'select': {
                const selectField = field as SelectFieldConfig;
                return (
                    <Form.Item key={index} label={field.label} name={field.name} rules={rules}>
                        <Select placeholder={selectField.placeholder} options={selectField.options} />
                    </Form.Item>
                );
            }
            case 'yearPicker': {
                const yearField = field as YearPickerFieldConfig;
                const disabledDate = yearField.allowedYears
                    ? YEAR_DISABLED_DATE_FNS[yearField.allowedYears]
                    : undefined;
                return (
                    <Form.Item key={index} label={field.label} name={field.name} rules={rules}>
                        <DatePicker
                            placeholder={yearField.placeholder}
                            allowClear={false}
                            style={{ width: 200, borderRadius: 6 }}
                            disabledDate={disabledDate}
                            picker="year"
                        />
                    </Form.Item>
                );
            }
            case 'dimSelect': {
                const dimField = field as DimSelectFieldConfig;
                const selectedDimension = dimField.dependsOn
                    ? {
                          value: dim1Value ?? '',
                          dimension: dimField.dependsOn.dimension,
                          attributeType: dimField.dependsOn.attributeType,
                      }
                    : undefined;
                return (
                    <Form.Item key={index} label={field.label} name={field.name} rules={rules}>
                        <DimSelect
                            dimension={dimField.dimension}
                            attributeType={dimField.attributeType}
                            lowestAttribute={dimField.lowestAttribute}
                            isMultiSelect={dimField.isMultiSelect}
                            placeholder={`Select ${field.label}`}
                            selectedDimension={selectedDimension}
                            useDimOptions={usePlanDimOptions}
                            enabled={open}
                            style={{ width: 200 }}
                        />
                    </Form.Item>
                );
            }
            default:
                return null;
        }
    };

    return (
        <Modal
            open={open}
            onCancel={onCancel}
            onOk={handleOk}
            okText={isEditMode ? 'Save' : 'Create Plan'}
            okButtonProps={{ loading: isPending, disabled: isOkDisabled }}
            cancelButtonProps={{ disabled: isPending }}
            closable={!isPending}
            maskClosable={!isPending}
            title={
                <div>
                    <p>{isEditMode ? 'Edit Plan' : 'Create New Plan'}</p>
                    {isPlanningGroupCreateMode && (
                        <p className="text-xs text-[#A49FA9]">{`Customer: ${planningGroupDetails.dim2_attrs.join(
                            ', ',
                        )} Category: ${planningGroupDetails.dim1_attrs.join(', ')} Year: ${
                            planningGroupDetails.plan_year
                        }`}</p>
                    )}
                </div>
            }
            width={600}
        >
            <div className="px-16">
                <Form
                    form={form}
                    name="create"
                    requiredMark={false}
                    labelCol={{ span: 8 }}
                    onFieldsChange={handleFieldsChange}
                    onFinish={handleFinish}
                >
                    {visibleFields.map(renderField)}
                </Form>
            </div>
        </Modal>
    );
};

export default CreatePlanModal;
